import { MS_PER_DAY } from "./iso-date";

/**
 * Cancellation policy: what a guest gets back, and until when.
 *
 * Deliberately a small closed set rather than a rules engine. A host picks one
 * of four familiar shapes — the same vocabulary the OTAs already taught them —
 * and the page states it in words the guest can act on. Per-night ladders,
 * partial-refund percentages and seasonal variants are a later product
 * decision, not a schema this has to anticipate.
 *
 * The policy is evaluated against the CHECK-IN date, never the booking date:
 * "free until 5 days before arrival" is what a guest reads and what every OTA
 * means by it.
 */

export const cancellationPolicyValues = [
  "flexible",
  "moderate",
  "strict",
  "non_refundable",
] as const;

export type CancellationPolicy = (typeof cancellationPolicyValues)[number];

/** Days before check-in that a full refund remains available. */
const FREE_CANCELLATION_DAYS: Record<CancellationPolicy, number | null> = {
  flexible: 1,
  moderate: 5,
  non_refundable: null,
  strict: 14,
};

/**
 * The instant a full refund stops being available, or null when the booking is
 * never refundable.
 */
export function refundableUntil(
  policy: CancellationPolicy,
  checkIn: Date
): Date | null {
  const days = FREE_CANCELLATION_DAYS[policy];
  if (days === null) {
    return null;
  }
  return new Date(checkIn.getTime() - days * MS_PER_DAY);
}

/** Whether cancelling right now still earns the money back. */
export function isRefundable(
  policy: CancellationPolicy,
  checkIn: Date,
  now: Date
): boolean {
  const deadline = refundableUntil(policy, checkIn);
  return deadline !== null && now < deadline;
}

/**
 * The policy in one sentence, for the booking page and the confirmation.
 *
 * Stated as what the guest gets rather than what the host allows — "free
 * cancellation until 5 days before check-in" is actionable where "moderate
 * policy" is not.
 */
export function describeCancellationPolicy(policy: CancellationPolicy): string {
  const days = FREE_CANCELLATION_DAYS[policy];
  if (days === null) {
    return "This booking is non-refundable.";
  }
  return days === 1
    ? "Free cancellation until 1 day before check-in."
    : `Free cancellation until ${days} days before check-in.`;
}
